/**
 * 📊 Station Results Tab - Cetalingua FM
 * 
 * Stats cards: files, GB, listeners, hours
 * Detection heatmap: calendar-style (like GitHub contributions)
 * Listeners by country: bar chart
 * 
 * Detection data comes from CSVs uploaded by admin, filtered by stationId
 * 
 * FIXES:
 * ✅ stationId converted to STRING for query (Firestore is type-strict)
 * ✅ Updated to use new detection document fields (totalSegments, segmentsWithCalls, activityPercent)
 */

import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { getSpeciesName } from '../../../data/speciesData';
import './StationResults.css';
import StationBroadcasts from './StationBroadcasts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const StationResults = ({ user, stationData }) => {
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalBytes: 0,
    totalListeners: 0,
    totalMinutesListened: 0,
  });
  const [listenersByCountry, setListenersByCountry] = useState([]);
  const [detections, setDetections] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedSpecies, setSelectedSpecies] = useState('all');
  const [detectedSpeciesList, setDetectedSpeciesList] = useState([]);
  const [loading, setLoading] = useState(true);

  const years = [2024, 2025, 2026];

  // Get stationId as STRING (Firestore is type-strict)
  const getStationIdString = () => {
    if (!stationData?.stationId) return null;
    return String(stationData.stationId);
  };

  useEffect(() => {
    loadStats();
    loadListeners();
    loadDetections();
  }, [user, stationData]);

  useEffect(() => {
    loadDetections();
  }, [selectedYear]);

  const loadStats = async () => {
    try {
      const stationId = getStationIdString();
      if (!stationId) return;

      // Load files - try both user.uid path and stationId path
      let filesSnap;
      try {
        const filesRef = collection(db, 'stations', stationId, 'files');
        filesSnap = await getDocs(filesRef);
      } catch (e) {
        // Fallback to user.uid if stationId doesn't work
        const filesRef = collection(db, 'stations', user.uid, 'files');
        filesSnap = await getDocs(filesRef);
      }

      let totalBytes = 0;
      filesSnap.forEach(doc => {
        const data = doc.data();
        totalBytes += data.totalSize || data.size || 0;
      });

      setStats(prev => ({
        ...prev,
        totalFiles: filesSnap.size,
        totalBytes,
      }));
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadListeners = async () => {
    try {
      const stationId = getStationIdString();
      if (!stationId) return;

      // Load listener stats
      let listenersSnap;
      try {
        const listenersRef = collection(db, 'stations', stationId, 'listeners');
        listenersSnap = await getDocs(listenersRef);
      } catch (e) {
        const listenersRef = collection(db, 'stations', user.uid, 'listeners');
        listenersSnap = await getDocs(listenersRef);
      }

      let totalListeners = 0;
      let totalMinutes = 0;
      const countries = {};

      listenersSnap.forEach(doc => {
        const data = doc.data();
        totalListeners++;
        totalMinutes += data.minutesListened || 0;
        if (data.country) {
          const code = data.country;
          if (!countries[code]) {
            countries[code] = { count: 0, name: data.countryName || code };
          }
          countries[code].count += 1;
        }
      });

      // Sort countries by count
      const sortedCountries = Object.entries(countries)
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 10)
        .map(([country, info]) => ({ country, count: info.count, name: info.name }));

      setStats(prev => ({
        ...prev,
        totalListeners,
        totalMinutesListened: totalMinutes,
      }));
      setListenersByCountry(sortedCountries);
    } catch (error) {
      console.error('Error loading listeners:', error);
    }
  };

  const loadDetections = async () => {
    setLoading(true);
    try {
      const stationId = getStationIdString();
      if (!stationId) {
        console.warn('No stationId available for detection query');
        setDetections([]);
        setLoading(false);
        return;
      }

      // Load detections for this station
      // ✅ FIX: Use STRING stationId for query
      const detectionsRef = collection(db, 'detections');
      const q = query(
        detectionsRef,
        where('stationId', '==', stationId), // STRING comparison
        where('year', '==', selectedYear)
      );

      const detectionsSnap = await getDocs(q);
      const detectionData = [];
      const speciesSet = new Set();

      detectionsSnap.forEach(doc => {
        const data = doc.data();
        detectionData.push(data);
        if (data.speciesId) {
          speciesSet.add(data.speciesId);
        }
      });

      console.log(`📊 Loaded ${detectionData.length} detection records for station ${stationId}, year ${selectedYear}`);

      setDetections(detectionData);
      setDetectedSpeciesList([...speciesSet]);
    } catch (error) {
      console.error('Error loading detections:', error);
      setDetections([]);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Adaptive listen-time label: shows minutes under an hour, then hours with
  // one decimal (e.g. "30 min", "1.5 h") so low early usage isn't stuck on "0".
  const formatListenTime = (totalMinutes) => {
    const m = Math.round(totalMinutes || 0);
    if (m < 60) return { value: `${m}`, unit: m === 1 ? 'Minute Listened' : 'Minutes Listened' };
    const hours = totalMinutes / 60;
    // one decimal, but drop a trailing .0 (e.g. 2.0 → "2")
    const label = Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
    return { value: label, unit: 'Hours Listened' };
  };

  // Build heatmap data: { 'month-day': count }
  // ✅ Updated to use new field names (segmentsWithCalls instead of count)
  const getHeatmapData = () => {
    const heatmap = {};

    detections.forEach(det => {
      // Filter by species if selected
      if (selectedSpecies !== 'all' && det.speciesId !== parseInt(selectedSpecies)) {
        return;
      }

      const date = new Date(det.date);
      const month = date.getMonth();
      const day = date.getDate();
      const key = `${month}-${day}`;

      // Use segmentsWithCalls (new) or count (legacy) or 1
      const detectionCount = det.segmentsWithCalls || det.count || 1;
      heatmap[key] = (heatmap[key] || 0) + detectionCount;
    });

    return heatmap;
  };

  const heatmapData = getHeatmapData();
  const maxDetections = Math.max(1, ...Object.values(heatmapData));
  const totalDetections = Object.values(heatmapData).reduce((a, b) => a + b, 0);

  // Color intensity based on count
  const getHeatmapColor = (count) => {
    if (!count) return 'var(--surface-light)';
    const intensity = Math.min(1, count / maxDetections);
    // Blue gradient
    if (intensity < 0.25) return 'rgba(0, 180, 216, 0.2)';
    if (intensity < 0.5) return 'rgba(0, 180, 216, 0.4)';
    if (intensity < 0.75) return 'rgba(0, 180, 216, 0.6)';
    return 'rgba(0, 119, 182, 0.9)';
  };

  return (
    <div className="station-results-tab">
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalFiles.toLocaleString()}</span>
            <span className="stat-label">Files Uploaded</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💾</div>
          <div className="stat-content">
            <span className="stat-value">{formatBytes(stats.totalBytes)}</span>
            <span className="stat-label">Total Data</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalListeners.toLocaleString()}</span>
            <span className="stat-label">Total Listeners</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏱️</div>
          <div className="stat-content">
            <span className="stat-value">{formatListenTime(stats.totalMinutesListened).value}</span>
            <span className="stat-label">{formatListenTime(stats.totalMinutesListened).unit}</span>
          </div>
        </div>
      </div>

      {/* Stage 3.6: one plain card per broadcast + public / private */}

      <StationBroadcasts stationId={stationData?.stationId} />


      <div className="results-columns">
        {/* Detection Heatmap */}
        <div className="heatmap-section">
          <div className="heatmap-header">
            <div>
              <h3>📊 Species Detections</h3>
              <p>Detection activity throughout the year</p>
            </div>
            <div className="heatmap-controls">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                value={selectedSpecies}
                onChange={(e) => setSelectedSpecies(e.target.value)}
              >
                <option value="all">All Species</option>
                {detectedSpeciesList.map(id => (
                  <option key={id} value={id}>{getSpeciesName(id)}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="heatmap-loading">Loading detections...</div>
          ) : detections.length === 0 ? (
            <div className="heatmap-empty">
              <p>No detection data for {selectedYear}</p>
              <span>Detection results are uploaded by admin after processing</span>
            </div>
          ) : (
            <>
              <div className="heatmap-summary">
                <span>{totalDetections.toLocaleString()} total detections</span>
                <span>Peak: {maxDetections} detections/day</span>
              </div>

              <div className="heatmap-container">
                <div className="heatmap-grid">
                  {/* Day labels header */}
                  <div className="heatmap-row header-row">
                    <div className="month-label"></div>
                    {DAYS.map(day => (
                      <div key={day} className="day-label">
                        {day % 5 === 0 ? day : ''}
                      </div>
                    ))}
                  </div>

                  {/* Month rows */}
                  {MONTHS.map((month, monthIndex) => (
                    <div key={month} className="heatmap-row">
                      <div className="month-label">{month}</div>
                      {DAYS.map(day => {
                        const key = `${monthIndex}-${day}`;
                        const count = heatmapData[key] || 0;
                        return (
                          <div
                            key={day}
                            className="heatmap-cell"
                            style={{ backgroundColor: getHeatmapColor(count) }}
                            title={`${month} ${day}: ${count} detections`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="heatmap-legend">
                  <span>Less</span>
                  <div className="legend-scale">
                    <div style={{ backgroundColor: 'rgba(0, 180, 216, 0.2)' }} />
                    <div style={{ backgroundColor: 'rgba(0, 180, 216, 0.4)' }} />
                    <div style={{ backgroundColor: 'rgba(0, 180, 216, 0.6)' }} />
                    <div style={{ backgroundColor: 'rgba(0, 119, 182, 0.9)' }} />
                  </div>
                  <span>More</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Listeners by Country */}
        <div className="listeners-section">
          <h3>🌍 Listeners by Country</h3>

          {listenersByCountry.length > 0 ? (
            <div className="country-list">
              {listenersByCountry.map(({ country, count, name }, index) => {
                const maxCount = listenersByCountry[0].count;
                const width = (count / maxCount) * 100;
                const code = (country || '').toLowerCase();
                return (
                  <div key={country} className="country-row">
                    <span className="country-rank">#{index + 1}</span>
                    {code && (
                      <img
                        className="country-flag"
                        src={`https://flagcdn.com/24x18/${code}.png`}
                        srcSet={`https://flagcdn.com/48x36/${code}.png 2x`}
                        width="24"
                        height="18"
                        alt={country}
                        style={{ borderRadius: 2, objectFit: 'cover' }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <span className="country-name">{name || country}</span>
                    <div className="country-bar-bg">
                      <div
                        className="country-bar-fill"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="country-count">{count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-listeners">
              <p>No listener data yet</p>
              <span>Listener locations are collected from the mobile app</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StationResults;
